import { ApolloQueryResult } from '@apollo/client';
import { Loader } from '@unmind/design-system';
import { Events } from 'App/Tracking/Events';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FeaturedPractitioners,
  FeaturedPractitioners_featuredPractitioners_edges_node as FeaturedPractitionersEdgeNode,
} from 'Services/BeGateway/Practitioner/__generated__/featuredPractitioners';
import {
  practitionersQuery_practitioners_edges_node as PractitionersEdgeNode,
  practitionersQuery,
} from 'Services/BeGateway/Practitioner/__generated__/practitionersQuery';
import SectionError from 'Shared/SectionError';
import { FilterKey } from '../../../types';
import { EmptyStateCard } from '../../EmptyStateCard';
import { PractitionerCard } from '../../PractitionerCard/PractitionerCard';
import { UL } from '../styles';

export const PractitionerCardListBase = ({
  trackingEvent,
  practitioners,
  hasFilters = false,
  removableFilterKey,
  onRemoveFilter,
  loading,
  error,
  refetch,
  singleRow,
}: {
  trackingEvent?: Events;
  practitioners: PractitionersEdgeNode[] | FeaturedPractitionersEdgeNode[];
  hasFilters?: boolean;
  removableFilterKey?: FilterKey;
  onRemoveFilter?(): void;
  loading: boolean;
  error: boolean;
  /** Show only as many cards as fill one row at the current width. */
  singleRow?: boolean;
  refetch(): Promise<
    ApolloQueryResult<practitionersQuery | FeaturedPractitioners>
  >;
}) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'practitioner.noPractitioners',
  });

  const emptyStateT = !hasFilters ? 'default' : 'filters';

  if (loading) {
    return <Loader />;
  }

  if (!practitioners.length && error) {
    return (
      <SectionError
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  if (!practitioners.length) {
    return (
      <EmptyStateCard
        title={t(`${emptyStateT}.title`)}
        subtitle={t(`${emptyStateT}.subtitle`)}
        action={
          hasFilters && removableFilterKey && onRemoveFilter
            ? {
                label: t(`filters.removeFilter.${removableFilterKey}`),
                onClick: onRemoveFilter,
              }
            : undefined
        }
      />
    );
  }

  return (
    <UL $singleRow={singleRow}>
      {practitioners.map(practitioner => (
        <PractitionerCard
          key={practitioner.id}
          practitioner={practitioner}
          trackingEvent={trackingEvent}
        />
      ))}
    </UL>
  );
};
